import api from './Api';
import type { Product as ProductType } from '../types/Product';

export interface DuplicateCheckParams {
  code?: string;
  model?: string;
  size?: string;
  color?: string;
  firm?: string;
  companyId?: number;
  excludeId?: number;
}

// Re-export Product type from types folder
export type Product = ProductType;

export const ProductApi = {
  // Get all products for a company
  getByCompany: (companyId: number, includeDeleted: boolean = false) =>
    api.get<Product[]>(`/companies/${companyId}/products${includeDeleted ? '?includeDeleted=true' : ''}`),

  // Get single product
  getById: (id: number) =>
    api.get<Product>(`/products/${id}`),

  // Resolve a barcode, QR payload, custom code, or product ID.
  scan: (code: string) =>
    api.post<Product>('/products/scan', { code: code.trim() }),

  // Create product
  create: (product: Product) =>
    api.post<Product>('/products', product),

  // Update product
  update: (id: number, product: Partial<Product>) =>
    api.put<Product>(`/products/${id}`, product),

  // Delete product
  delete: (id: number) =>
    api.delete(`/products/${id}`),

  // Soft delete product (mark as deleted)
  softDelete: (id: number) =>
    api.patch(`/products/${id}/delete`),

  // Toggle hidden state (hide/unhide)
  setHidden: (id: number, hidden: boolean) =>
    api.patch(`/products/${id}/hidden`, null, { params: { hidden } }),

  // Hard (permanent) delete — removes product and all its DB connections
  hardDelete: (id: number) =>
    api.delete(`/products/${id}/permanent`),

  // Check for duplicates
  checkDuplicate: (params: DuplicateCheckParams) =>
    api.get<Product[]>('/products/check-duplicate', { params }),

  // Upload product image
  uploadImage: (productId: number, formData: FormData) =>
    api.post(`/images/${productId}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Delete product image
  deleteImage: (productId: number) =>
    api.delete(`/images/${productId}/image`),
};
